import clsx from 'clsx';
import { useRef } from 'react';

import Breadcrumbs from 'src/components/common/breadcrumbs';
import { DomainBadge } from 'src/components/common/domain-badge';
import { Tooltip } from 'src/components/tooltip';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import { useTrimmedElementCheck } from 'src/hooks/use-trimmed-element-check';
import {
  DesmosAssignment,
  SingleAssignment,
} from 'src/interfaces/assignment.types';
import { TaughtLessonAssignment } from 'src/interfaces/class-stats.types';
import {
  getAssignmentLessonNumber,
  getAssignmentSubTitle,
  getAssignmentTitle,
  isDesmosAssignmentContent,
} from 'src/utils/assignments.utils';

import './subtitle.scss';

type SubtitleProps = {
  assignment: SingleAssignment | TaughtLessonAssignment | DesmosAssignment;
  assignmentDomain?: string;
  unitTitle?: string;
  subtitle?: string;
  assignmentTitle?: string;
  isStatusChangeDialogVisible?: boolean;
};

export const Subtitle = ({
  assignmentDomain,
  unitTitle,
  assignment,
  subtitle,
  assignmentTitle,
  isStatusChangeDialogVisible,
}: SubtitleProps) => {
  const isMiddleSchoolView = useIsMiddleSchoolView();

  const getDesmosSubtitleText = () => {
    return subtitle ?? '';
  };
  const breadcrumbsUnitTitle = getAssignmentTitle(unitTitle);
  const lessonNumber = isDesmosAssignmentContent(assignment.content)
    ? getDesmosSubtitleText()
    : getAssignmentLessonNumber(assignmentTitle);

  const assignmentSubtitleRef = useRef<HTMLParagraphElement>(null);

  const isAssignmentSubtitleTrimmed = useTrimmedElementCheck(
    assignmentSubtitleRef,
  );

  const assignmentSubtitleLabel = isDesmosAssignmentContent(assignment.content)
    ? getDesmosSubtitleText()
    : getAssignmentSubTitle(assignment.content);

  return (
    <div
      data-testid="assignment-subtitle"
      className={clsx('assignment__subtitle', {
        'assignment__subtitle--middle-school': isMiddleSchoolView,
        'assignment__subtitle--reduced': isStatusChangeDialogVisible,
      })}
    >
      <div
        className={clsx('assignment__subtitle-domain-badge', {
          'assignment__subtitle-domain-badge--middle-school':
            isMiddleSchoolView,
        })}
      >
        <DomainBadge
          domain={assignmentDomain}
          isMiddleSchool={isMiddleSchoolView}
          subject={assignment.content.subject}
        />
      </div>
      {isMiddleSchoolView ? (
        <Breadcrumbs
          unitTitle={breadcrumbsUnitTitle}
          lessonNumber={lessonNumber}
        />
      ) : (
        <Tooltip
          title={assignmentSubtitleLabel ?? ''}
          classNames={{
            tooltip: 'sh-mobile-tooltip-bottom-left',
            container: 'assignment__subtitle-text-tooltip-container',
          }}
          suppress={!isAssignmentSubtitleTrimmed}
        >
          <p
            ref={assignmentSubtitleRef}
            className="assignment__subtitle-text"
            tabIndex={isAssignmentSubtitleTrimmed ? 0 : -1}
          >
            {assignmentSubtitleLabel}
          </p>
        </Tooltip>
      )}
    </div>
  );
};
